import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { 
  LogOut, 
  MessageSquare, 
  Settings,
  Menu,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminLayout = ({ children, username }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/admin/logout?token=${token}`, { method: 'POST' });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/login');
  };

  const texts = {
    tr: {
      contacts: 'İletişim',
      settings: 'Ayarlar',
      logout: 'Çıkış',
    },
    en: {
      contacts: 'Contacts',
      settings: 'Settings',
      logout: 'Logout',
    },
  };

  const t = texts[language];

  const menuItems = [
    { 
      id: 'contacts', 
      label: t.contacts, 
      icon: MessageSquare, 
      path: '/admin' 
    },
    { 
      id: 'settings', 
      label: t.settings, 
      icon: Settings, 
      path: '/admin/settings' 
    },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-20">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="mobile-menu-toggle"
              >
                {sidebarOpen ? (
                  <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                ) : (
                  <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                )}
              </button>
              
              <a href="/" className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_platform-control-9/artifacts/v13sl9x3_Ads%C4%B1z%20tasar%C4%B1m%20%2815%29.png"
                  alt="AgrosTech"
                  className="w-8 h-8 object-contain dark:drop-shadow-[0_0_6px_rgba(255,255,255,0.2)]"
                />
                <span className="text-lg font-bold tracking-tight">
                  <span className="text-slate-900 dark:text-white">Agros</span>
                  <span className="text-indigo-600 dark:text-indigo-400">Tech</span>
                </span>
              </a>
              <span className="hidden sm:inline text-slate-300 dark:text-slate-700">|</span>
              <span className="hidden sm:inline text-sm font-medium text-slate-600 dark:text-slate-400">Admin Panel</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="hidden sm:inline text-sm text-slate-600 dark:text-slate-400">
                {username}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 sm:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t.logout}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-10 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-16 left-0 z-10 h-[calc(100vh-4rem)] w-64 
          bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all
                    ${active 
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }
                  `}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
};
